import type { ScoreRecord } from "../types";
import { supabase } from "./supabaseClient";

// Shape of a row in the Supabase `rankings` table (snake_case columns).
interface RemoteRankingRow {
  alias: string;
  device_id: string | null;
  score: number;
  elapsed: number | null;
  distance: number | null;
  victory: boolean | null;
  rank_id: string | null;
  rank_name: string | null;
  collected_isotype_percent: number | null;
  beat_sync_percent: number | null;
  best_combo: number | null;
}

// Row as returned by get_ranking_view: a ranking row plus its real position.
interface RemoteRankedRow extends RemoteRankingRow {
  pos: number;
}

// JSON payload returned by the get_ranking_view RPC.
interface RemoteRankingView {
  top10: RemoteRankedRow[] | null;
  me: RemoteRankedRow | null;
  neighbors: RemoteRankedRow[] | null;
  total: number | null;
}

// A ScoreRecord enriched with its real position in the global ranking.
export type RankedRecord = ScoreRecord & { position: number };

// Parsed view consumed by OverlayUi to render the Top 10 + "mi posición" zone.
export interface RankingView {
  top10: RankedRecord[];
  me: RankedRecord | null;
  neighbors: RankedRecord[];
  total: number;
}

const TOP_LIMIT = 10;

// Online global ranking (Etapa 2b). All methods are non-blocking and never
// throw: the local save (Etapa 1) and the game-over flow stay intact even if
// the network fails. Assumes internet for now (offline queue is a future task).
export class GlobalRanking {
  static get enabled(): boolean {
    return supabase !== null;
  }

  // Conditional upsert via the SQL function `upsert_ranking` (updates only when
  // the new score is higher). Returns true on success, false on any failure.
  static async upload(record: ScoreRecord): Promise<boolean> {
    if (!supabase) return false;
    try {
      const { error } = await supabase.rpc("upsert_ranking", {
        p_alias: record.alias,
        p_device_id: record.deviceId,
        p_score: Math.round(record.score),
        p_elapsed: record.elapsed,
        p_distance: record.distance,
        p_victory: record.victory,
        p_rank_id: record.rankId ?? null,
        p_rank_name: record.rankName ?? null,
        p_collected_isotype_percent: record.collectedIsotypePercent ?? null,
        p_beat_sync_percent: record.beatSyncPercent ?? null,
        p_best_combo: record.bestCombo ?? null
      });
      if (error) {
        console.error("[GlobalRanking] upload error:", error);
        return false;
      }
      return true;
    } catch (error) {
      console.error("[GlobalRanking] upload exception:", error);
      return false;
    }
  }

  // Top 10 global by score desc, mapped to the ScoreRecord shape renderLeaderboard
  // expects. Returns null on failure (caller shows "RANKING NO DISPONIBLE") and
  // [] when the table is simply empty.
  static async fetchTop10(): Promise<ScoreRecord[] | null> {
    if (!supabase) return null;
    try {
      const { data, error } = await supabase
        .from("rankings")
        .select("*")
        .order("score", { ascending: false })
        .limit(TOP_LIMIT);
      if (error) {
        console.error("[GlobalRanking] fetch error:", error);
        return null;
      }
      return (data ?? []).map((row) => GlobalRanking.toRecord(row as RemoteRankingRow));
    } catch (error) {
      console.error("[GlobalRanking] fetch exception:", error);
      return null;
    }
  }

  // Single-call ranking view (Top 10 + the player's own position/neighbors) via
  // the get_ranking_view RPC. `alias` identifies the player (null when they have
  // no saved record -> `me`/`neighbors` come back empty). Same non-blocking
  // contract as fetchTop10: returns null on failure (caller shows the empty
  // state), never throws.
  static async fetchRankingView(alias: string | null): Promise<RankingView | null> {
    if (!supabase) return null;
    try {
      const { data, error } = await supabase.rpc("get_ranking_view", { p_alias: alias });
      if (error) {
        console.error("[GlobalRanking] ranking view error:", error);
        return null;
      }
      const view = (data ?? null) as RemoteRankingView | null;
      if (!view) return { top10: [], me: null, neighbors: [], total: 0 };
      return {
        top10: (view.top10 ?? []).map(GlobalRanking.toRanked),
        me: view.me ? GlobalRanking.toRanked(view.me) : null,
        neighbors: (view.neighbors ?? []).map(GlobalRanking.toRanked),
        total: view.total ?? 0
      };
    } catch (error) {
      console.error("[GlobalRanking] ranking view exception:", error);
      return null;
    }
  }

  private static toRanked(row: RemoteRankedRow): RankedRecord {
    return { ...GlobalRanking.toRecord(row), position: row.pos };
  }

  private static toRecord(row: RemoteRankingRow): ScoreRecord {
    return {
      schemaVersion: 2,
      id: row.alias,
      alias: row.alias,
      deviceId: row.device_id ?? "",
      score: row.score,
      elapsed: row.elapsed ?? 0,
      distance: row.distance ?? 0,
      victory: Boolean(row.victory),
      rankId: row.rank_id ?? undefined,
      rankName: row.rank_name ?? undefined,
      collectedIsotypePercent: row.collected_isotype_percent ?? undefined,
      beatSyncPercent: row.beat_sync_percent ?? undefined,
      bestCombo: row.best_combo ?? undefined,
      createdAt: ""
    };
  }
}
